import { MagnifyingGlassIcon } from "@heroicons/react/24/solid";
import { useEffect, useReducer, useMemo } from "react";
import BoxWrapper from "../../components/BoxWrapper";
import { SidebarIcon } from "../../components/icons/SidebarIcons";
import Pagination from "../../components/Pagination";
import RenderTable from "../../components/RenderTable";
import {
  bulkDeleteReducer,
  bulkDeleteState,
} from "../../context/bulkDeleteReducer";
import { RideStatusLabels } from "../../helpers/enums";
import { pluralize, Query } from "../../helpers/general";
import { useRides } from "../../hooks/useRides";
import { general } from "../../locales/general";
import AdminRoute from "../auth/RestrictedRoute";
import RidersTable from "./RidersTable";
import qs from "qs";
import { useNavigate, useSearchParams } from "react-router-dom";
import CalendarDateFilters from "../../components/Calendar";

const pageSize = 10;

export default function Riders() {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const initialQueries: Partial<Query> = useMemo(() => {
    const parsed = qs.parse(searchParams.toString());

    return {
      ...parsed,
      includeHistory: "false",
      page: parsed.page ? Number(parsed.page as string) : undefined,
    };
  }, [searchParams]);

  const { queries, addQuery, isLoading, rides, setDates } =
    useRides(initialQueries);

  const [ridesToBeDeleted, dispatch] = useReducer(
    bulkDeleteReducer,
    bulkDeleteState,
  );

  useEffect(() => {
    const updatedQueries = { ...queries };
    if (!updatedQueries.page) {
      updatedQueries.page = 1;
    }

    const newSearch = qs.stringify(updatedQueries, { skipNulls: true });
    navigate(`?${newSearch}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(queries), navigate]);

  const handleFilterChange = (key: any, value: any) => {
    addQuery(key, value);
    if (key !== "page") {
      addQuery("page", 1);
    }
  };

  const SrerchDefaulValue = searchParams.get("query");

  const handleSelect = (dates: { from: string; to: string }) => {
    addQuery("startDate", dates.from);
    addQuery("endDate", dates.to);
    addQuery("page", 1);
    setDates(dates);
  };

  return (
    <AdminRoute>
      <section>
        <h5 className="fs-4 fw-bold mb-2">Trajets</h5>
        <BoxWrapper>
          {/* Header */}
          <div className="d-flex justify-content-between align-items-baseline before-table">
            <div className="d-flex align-items-center gap-4 p-3 delete-add"></div>
            <div
              className="d-flex align-items-center justify-content-center gap-3 bg-gradient-primary p-3"
              style={{ borderTopRightRadius: "8px", alignSelf: "flex-start" }}
            >
              <SidebarIcon.RidesSVG className="fill-white" />
              <p className="fs-5 fw-bold m-0 text-white text-pre">
                {rides?.meta?.totalItems}{" "}
                {pluralize("Trajet", rides?.meta?.totalItems)}
              </p>
            </div>
          </div>

          {/* Filters */}
          <div className="d-flex align-items-center gap-4 px-3 mb-3">
            {/* <DateFilters
              label="Date"
              onChange={setDates}
              // onPeriodChange={(period) => {
              //   addQuery("period", period || undefined);
              //   if (period) {
              //     addQuery("page", 1);
              //   }
              // }}
              defaultPeriod={queries.period}
            /> */}

            <CalendarDateFilters
              label="Période"
              onChange={handleSelect}
              defaultStartDate={queries.startDate as string}
              defaultEndDate={queries.endDate as string}
            />

            <div>
              <label htmlFor="status">Statut</label>
              <select
                id="status"
                value={queries.status || ""}
                onChange={(e) =>
                  handleFilterChange("status", e.target.value || undefined)
                }
              >
                <option value="">Tous</option>
                {Object.entries(RideStatusLabels).map(([key, value]) => (
                  <option key={key} value={key}>
                    {value}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="paymentMethod">Mode de paiement</label>
              <select
                id="paymentMethod"
                value={queries.paymentMethod || ""}
                onChange={(e) =>
                  handleFilterChange(
                    "paymentMethod",
                    e.target.value || undefined,
                  )
                }
              >
                <option value="">Tous</option>
                <option value="cash">Espèces</option>
                <option value="user_balance">Solde utilisateur</option>
              </select>
            </div>

            <div>
              <label htmlFor="isValid" className="ms-1">
                Le trajet est-il valid ?
              </label>
              <select
                name="isValid"
                id="isValid"
                onChange={(e) => addQuery("isValidRide", e.target.value as any)}
              >
                <option value={""}>Tous</option>
                <option value="true">Valide</option>
                <option value="false">Non valide</option>
                <option value="pending">En attente</option>
              </select>
            </div>

            <div>
              <label className="mb-1  " htmlFor="query">
                Rechercher
              </label>
              <div className="search-form">
                <div className="input-field p-1">
                  <MagnifyingGlassIcon color="gray" width={20} />
                  <input
                    id="query"
                    type="text"
                    placeholder="Rechercher"
                    onChange={(e) =>
                      handleFilterChange("query", e.target.value || undefined)
                    }
                    value={SrerchDefaulValue!}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="table-responsive overflow-auto">
            <RenderTable
              loading={isLoading}
              render={() =>
                rides?.items && rides?.items.length > 0 ? (
                  <RidersTable
                    riders={rides.items}
                    onRiderSelect={dispatch}
                    selectedRides={ridesToBeDeleted.ids}
                  />
                ) : (
                  <p className="text-center fs-5 my-2">{general.fr.noData}</p>
                )
              }
            />
          </div>
        </BoxWrapper>

        {/* Pagination */}
        <Pagination
          currentPage={Number(queries.page) ?? 1}
          onPageChange={(page) => handleFilterChange("page", page)}
          siblingCount={1}
          totalCount={rides?.meta?.totalPages || 0}
          pageSize={pageSize}
        />
      </section>
    </AdminRoute>
  );
}
